import React from 'react';
import { Link2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { OpenCandidatesActionFn, ProgramControlCommonProps, SubmitReportFn } from '../controlTypes';
import { ODR_WORKFLOW_PRESENTATIONS, OdrWorkflowStatusIcon } from './odrWorkflowPresentation';

type OdrProgramControlsProps = ProgramControlCommonProps & {
  onOpenCandidatesAction: OpenCandidatesActionFn;
  onSubmitReport: SubmitReportFn;
};

const reviewStatusOptions = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'emailed', label: 'Emailed' },
  { value: 'complete', label: 'Complete' },
  { value: 'not_eligible', label: 'Not Eligible' },
  { value: 'not_clinically_eligible', label: 'Not Clinically Eligible' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'released_sentenced', label: 'Released/Sentenced' },
  { value: 'already_odr', label: 'Already ODR' },
];

const YesNoFilter = ({
  name,
  label,
  value,
  onChange,
}: {
  name: string;
  label: string;
  value: 'all' | 'y' | 'n';
  onChange: (value: 'all' | 'y' | 'n') => void;
}) => (
  <div className="flex items-center gap-2.5 whitespace-nowrap">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">{label}</div>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--neutral-gray-120)]">
      <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name={name} checked={value === 'all'} onChange={() => onChange('all')} />All</label>
      <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name={name} checked={value === 'y'} onChange={() => onChange('y')} />Yes</label>
      <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name={name} checked={value === 'n'} onChange={() => onChange('n')} />No</label>
    </div>
  </div>
);

export const OdrProgramControls = ({
  values,
  onFilterChange,
  actionRowClass,
  renderStandardActionButtons,
  renderReportActionLabel,
  onOpenCandidatesAction,
  onSubmitReport,
}: OdrProgramControlsProps) => (
  <div className="mb-2 flex flex-wrap items-end justify-between gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-3 py-2">
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-y-2 [&>div+div]:ml-4 [&>div+div]:border-l [&>div+div]:border-[var(--neutral-gray-60)] [&>div+div]:pl-4">
        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <label htmlFor="odr-candidates-workflow-filter" className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Workflow</label>
          {values.odrWorkflowFilter !== 'all' && (
            <OdrWorkflowStatusIcon stage={values.odrWorkflowFilter} className="h-4 w-4" />
          )}
          <select
            id="odr-candidates-workflow-filter"
            aria-label="Workflow status"
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            value={values.odrWorkflowFilter}
            onChange={(event) => onFilterChange('odrWorkflowFilter', event.target.value)}
          >
            <option value="all">All workflow states</option>
            {ODR_WORKFLOW_PRESENTATIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Hub</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--neutral-gray-120)]">
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-hub" checked={values.odrHubFilter === 'all'} onChange={() => onFilterChange('odrHubFilter', 'all')} />All</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-hub" checked={values.odrHubFilter === 'hub1'} onChange={() => onFilterChange('odrHubFilter', 'hub1')} />Hub 1</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-hub" checked={values.odrHubFilter === 'hub2'} onChange={() => onFilterChange('odrHubFilter', 'hub2')} />Hub 2</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-hub" checked={values.odrHubFilter === 'hub3'} onChange={() => onFilterChange('odrHubFilter', 'hub3')} />Hub 3</label>
          </div>
        </div>

        <YesNoFilter name="odr-mhd" label="ODR/MHD" value={values.odrMhdFilter} onChange={(value) => onFilterChange('odrMhdFilter', value)} />

        <YesNoFilter name="odr-cancelled" label="Cancelled" value={values.odrCancelledFilter} onChange={(value) => onFilterChange('odrCancelledFilter', value)} />

        <YesNoFilter name="odr-enrolled" label="Enrolled" value={values.odrEnrolledFilter} onChange={(value) => onFilterChange('odrEnrolledFilter', value)} />

        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">IH+</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--neutral-gray-120)]">
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-ih" checked={values.odrIhFilter === 'all'} onChange={() => onFilterChange('odrIhFilter', 'all')} />All</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-ih" checked={values.odrIhFilter === 'Y'} onChange={() => onFilterChange('odrIhFilter', 'Y')} />Yes</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-ih" checked={values.odrIhFilter === 'N'} onChange={() => onFilterChange('odrIhFilter', 'N')} />No</label>
          </div>
        </div>

        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">IMD</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--neutral-gray-120)]">
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-imd" checked={values.odrImdFilter === 'all'} onChange={() => onFilterChange('odrImdFilter', 'all')} />All</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-imd" checked={values.odrImdFilter === 'Y'} onChange={() => onFilterChange('odrImdFilter', 'Y')} />Yes</label>
            <label className="flex items-center gap-1 whitespace-nowrap"><input type="radio" name="odr-imd" checked={values.odrImdFilter === 'N'} onChange={() => onFilterChange('odrImdFilter', 'N')} />No</label>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-y-2 [&>div+div]:ml-4 [&>div+div]:border-l [&>div+div]:border-[var(--neutral-gray-60)] [&>div+div]:pl-4">
        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Review</div>
          <select
            aria-label="Review status"
            className="h-8 rounded-md border border-input bg-background px-2 text-[11px]"
            value={values.odrReviewStatusFilter}
            onChange={(event) => onFilterChange('odrReviewStatusFilter', event.target.value)}
          >
            {reviewStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Reviewer</div>
          <Input
            aria-label="Reviewer"
            className="h-8 w-28 text-[11px]"
            value={values.odrReviewerFilter}
            onChange={(event) => onFilterChange('odrReviewerFilter', event.target.value)}
          />
        </div>

        <YesNoFilter name="odr-emailed-pd" label="Emailed PD" value={values.odrEmailedPdFilter} onChange={(value) => onFilterChange('odrEmailedPdFilter', value)} />
        <YesNoFilter name="odr-added-to-dd" label="Added to DD" value={values.odrAddedToDdFilter} onChange={(value) => onFilterChange('odrAddedToDdFilter', value)} />
        <YesNoFilter name="odr-eligible" label="Eligible" value={values.odrEligibleFilter} onChange={(value) => onFilterChange('odrEligibleFilter', value)} />
        <YesNoFilter name="odr-homeless" label="Homeless" value={values.odrHomelessFilter} onChange={(value) => onFilterChange('odrHomelessFilter', value)} />
        <YesNoFilter name="odr-pre-plea" label="Pre-Plea" value={values.odrPrePleaFilter} onChange={(value) => onFilterChange('odrPrePleaFilter', value)} />
      </div>
    </div>

    <div className={actionRowClass}>
      {renderStandardActionButtons()}
      <Button asChild type="button" variant="outline" size="sm" className="h-8">
        <Link id="odr-candidates-participant-link-repair" to="/candidates/odr/participant-link-repair">
          <Link2 className="h-4 w-4" />Repair participant links
        </Link>
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onOpenCandidatesAction('court-list-report')}>
        {renderReportActionLabel('+ Court List Report')}
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onSubmitReport({ action: 'all-report', download: '1' })}>
        {renderReportActionLabel('Download Candidates List')}
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onSubmitReport({ action: 'list-releases', download: '1' })}>
        {renderReportActionLabel('Download Candidates List Releases')}
      </Button>
      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onSubmitReport({ action: 'all-report', download: '1' })}>
        {renderReportActionLabel('All')}
      </Button>
    </div>
  </div>
);
